<script setup lang="ts">
/**
 * Gift quiz "Shopping for someone?" — full-screen layer (Figma Quizzes › Gift quiz ·
 * desktop 3222:17260 / mobile 3222:105807). Occasion, budget and note are asked every
 * time; the person's styles / colors / brands live on their card (T-132).
 * T-135 modes (`store.mode`): Find a gift — a tuned person gets 07b instead of 08–10;
 * Tune only — 08–10b, Save; Add person — Who → 08–10, Save. A new person is saved right
 * after Who and each taste step is saved as it is passed, so leaving keeps both (R288).
 * The ideas come from the catalogue search (BE-153); the result opens the Gift ideas page.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useFairCompact } from '@/composables/useFairCompact'
import { hasSurveysApi } from '@/features/survey/flags'
import { isPersonTuned, usePersonTasteResolver, usePersonTasteStore, type PersonTaste } from '@/features/people'
import { useCreatePersonMutation, usePatchPersonMutation } from '@/mutations/profile/usePeopleMutations'
import { useGiftConfigQuery } from '@/queries/survey'
import { useAvailableBrandsQuery, useAvailableColorsQuery, useAvailableStylesQuery } from '@/queries/my-style'
import { usePeopleQuery } from '@/queries/profile/usePeopleQuery'
import type { Person } from '@/types/people'
import {
  FairQuizHeader,
  FairQuizLayer,
  FairQuizLeaveDialog,
  FairQuizStep,
  FairQuizWelcome,
  settleFairQuizHistory,
} from '@/ui/patterns/FairQuiz'
import { FairTasteDetails } from '@/ui/patterns/FairTastePreview'
import { useFairToast } from '@/ui/patterns/FairToast'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairChip } from '@/ui/primitives/FairChip'
import { FairInputText } from '@/ui/primitives/FairInputText'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'
import { FairThemeCard } from '@/ui/primitives/FairThemeCard'
import { readStoredCurrency } from '@/ui/tokens/fair-locale-menu.data'
import FairProfileV2SelectionPicker from '@/widgets/profile/fair-v2/FairProfileV2SelectionPicker.vue'
import type { FairProfileV2PickerItem } from '@/widgets/profile/fair-v2/fair-profile-v2-my-style.data'
import {
  emptyFairProfileV2PersonDraft,
  fairProfileV2VisiblePeople,
  isFairProfileV2PersonComplete,
  toFairProfileV2PersonPayload,
  writeFairProfileV2ChildSex,
  type FairProfileV2PersonDraft,
} from '@/widgets/profile/fair-v2/fair-profile-v2-people.data'
import { mapFairSearchV2TeaserToGridCard } from '@/widgets/search/fair-v2/fair-search-v2-recs.data'
import type { FairSearchV2ProductCard } from '@/widgets/search/fair-v2/fair-search-v2.data'
import { fetchGiftIdeas, fetchGiftQuizIdeas, saveGiftQuizIdeas } from '../api/gift-quiz'
import { useGiftQuizStore } from '../store'
import {
  emptyGiftQuizAnswers,
  giftQuizAnswerChips,
  giftQuizAudience,
  giftQuizBudgetCurrency,
  giftQuizBudgetOptions,
  giftQuizOccasionOptions,
  giftQuizPersonTaste,
  giftQuizProgress,
  giftQuizPronoun,
  giftQuizIdeasParams,
  giftQuizSearchRequest,
  giftQuizStepAfter,
  giftQuizSteps,
  type GiftQuizAnswers,
  type GiftQuizRecipient,
  type GiftQuizStep,
} from '../gift-quiz.data'
import GiftQuizPreview from './GiftQuizPreview.vue'
import GiftQuizWho from './GiftQuizWho.vue'
import './gift-quiz.css'

/** Same life as People's `03 Person added` toast (plain, no Undo). */
const TOAST_LIFE_MS = 4000

const store = useGiftQuizStore()
const { t } = useI18n()
const router = useRouter()
const toast = useFairToast()
const compact = useFairCompact()
/** The person's styles / colors / brands — shared with People and the search selector (T-132). */
const personTaste = usePersonTasteStore()
const tasteResolver = usePersonTasteResolver()

const peopleQuery = usePeopleQuery()
const stylesQuery = useAvailableStylesQuery()
const colorsQuery = useAvailableColorsQuery()
const brandsQuery = useAvailableBrandsQuery()
const createPersonMutation = useCreatePersonMutation()
const patchPersonMutation = usePatchPersonMutation()
const giftApi = hasSurveysApi('gift_recipients')

/** The account's currency (BE-201 keeps the stored one in sync with the profile). */
const currency = ref('USD')
/** BE-153 — occasions and budget bands from `GET /gift/config`, labels in the request language. */
const giftConfigQuery = useGiftConfigQuery(currency, computed(() => store.isOpen && giftApi))
const giftConfig = computed(() => (giftApi ? giftConfigQuery.data.value ?? null : null))
const occasionOptions = computed(() =>
  giftQuizOccasionOptions(giftConfig.value, (key) => t(`gift_quiz.occasion.options.${key}`)),
)
const step = ref<GiftQuizStep>('welcome')

/* ── Flow (T-135) ─────────────────────────────────────────────────────── */

/** 07b applies: the person had taste when Budget was passed (fixed for the run). */
const tasteCheck = ref(false)
/** 07b › Change taste: 08–10 follow 07b. */
const changeTaste = ref(false)
const steps = computed(() =>
  giftQuizSteps({ mode: store.mode, tasteCheck: tasteCheck.value, changeTaste: changeTaste.value }),
)
/** Tune only / Add person end on 10b with Save instead of the search. */
const isLastStep = computed(() => giftQuizStepAfter(steps.value, step.value, 1) === null)

/* ── Who ──────────────────────────────────────────────────────────────── */

const people = computed<Person[]>(() => fairProfileV2VisiblePeople(peopleQuery.data.value ?? []))
const selectedId = ref<number | null>(null)
const draft = ref<FairProfileV2PersonDraft>(emptyFairProfileV2PersonDraft())
const recipient = ref<GiftQuizRecipient>({ id: null, name: '', audience: null })
const saving = ref(false)
/** The person this run created on Who (R288): Back to Who must not add them twice. */
const createdId = ref<number | null>(null)

/** Add person shows only the fields (and the person once created); Find a gift — everyone. */
const whoPeople = computed(() =>
  store.mode === 'add-person' ? people.value.filter((person) => person.id === createdId.value) : people.value,
)

/** New person: name, relation, age range and gender are required (03 / 04 notes). */
const canContinueWho = computed(
  () => selectedId.value !== null || (isFairProfileV2PersonComplete(draft.value) && Boolean(draft.value.gender)),
)

function recipientOf(person: Person): GiftQuizRecipient {
  return { id: person.id, name: person.profileName, audience: giftQuizAudience(person.gender) }
}

async function continueWho() {
  const chosen = people.value.find((person) => person.id === selectedId.value)
  if (chosen) {
    recipient.value = recipientOf(chosen)
  } else if (selectedId.value !== null && selectedId.value === createdId.value) {
    // Created earlier in this run, People not refreshed yet: the recipient is already set.
  } else {
    // T-135: the new person is saved to People right after step 1 (R288).
    saving.value = true
    try {
      const created = await createPersonMutation.mutateAsync(toFairProfileV2PersonPayload(draft.value))
      const id = created?.id ?? null
      if (!giftApi && id !== null && (draft.value.gender === 'girl' || draft.value.gender === 'boy')) {
        writeFairProfileV2ChildSex(id, draft.value.gender)
      }
      recipient.value = {
        id,
        name: draft.value.name.trim(),
        audience: giftQuizAudience(toFairProfileV2PersonPayload(draft.value).gender),
      }
      // Back to Who shows them as the chosen card, not the filled fields.
      createdId.value = id
      if (id !== null) {
        selectedId.value = id
        draft.value = emptyFairProfileV2PersonDraft()
      }
    } catch (error) {
      console.error('Gift quiz: person not added', error)
      toast.show({ tone: 'warning', message: t('gift_quiz.who.save_error') })
      return
    } finally {
      saving.value = false
    }
  }
  prefillTaste()
  forward()
}

/* ── Answers ──────────────────────────────────────────────────────────── */

const answers = ref<GiftQuizAnswers>(emptyGiftQuizAnswers())
const stylePicks = ref<number[]>([])
const colorPicks = ref<number[]>([])
const brandPicks = ref<number[]>([])

/**
 * "Prefilled from Anna's card". With BE-153 (`gift_recipients`) People stores the taste
 * (`style_ids` / `color_ids` / `brand_ids`): a tuned card wins over the browser copy and
 * is written into the shared store, so 07b, People and the search selector agree.
 */
function prefillTaste() {
  const person = people.value.find((item) => item.id === recipient.value.id) ?? null
  const fromApi = giftApi ? giftQuizPersonTaste(person) : null
  if (fromApi && isPersonTuned(fromApi)) personTaste.setTaste(recipient.value.id, fromApi)
  const taste = personTaste.get(recipient.value.id)
  stylePicks.value = [...taste.styleIds]
  colorPicks.value = [...taste.colorIds]
  brandPicks.value = [...taste.brandIds]
}

/** The person's taste, budget and occasion go back to People (BE-153 / BE-150). */
function saveTasteToPerson(): Promise<unknown> {
  const id = recipient.value.id
  if (!giftApi || id === null) return Promise.resolve()
  const budget = budgetOptions.value.find((option) => option.value === answers.value.budget)
  return patchPersonMutation
    .mutateAsync({
      id,
      patch: {
        style_ids: [...stylePicks.value],
        color_ids: [...colorPicks.value],
        brand_ids: [...brandPicks.value],
        ...(answers.value.occasion && answers.value.occasion !== 'other' ? { occasion: answers.value.occasion } : {}),
        ...(budget
          ? {
              price_range_min: budget.min ?? null,
              price_range_max: budget.max ?? null,
              budget_currency: giftQuizBudgetCurrency(giftConfig.value, currency.value),
            }
          : {}),
      },
    })
    .catch((error) => console.warn('Gift quiz: the person\'s taste was not saved', error))
}

/** T-135: each taste step goes to the card as it is passed (Continue / Save). */
function saveTasteStep() {
  const patch: Partial<PersonTaste> | null =
    step.value === 'styles'
      ? { styleIds: stylePicks.value }
      : step.value === 'colors'
        ? { colorIds: colorPicks.value }
        : step.value === 'brands'
          ? { brandIds: brandPicks.value }
          : null
  if (!patch) return
  personTaste.patchTaste(recipient.value.id, patch)
  // BE-153: the card on People is the durable copy (Tune only / Add person end without a search).
  const id = recipient.value.id
  if (giftApi && id !== null) {
    patchPersonMutation
      .mutateAsync({
        id,
        patch: {
          ...(patch.styleIds ? { style_ids: [...patch.styleIds] } : {}),
          ...(patch.colorIds ? { color_ids: [...patch.colorIds] } : {}),
          ...(patch.brandIds ? { brand_ids: [...patch.brandIds] } : {}),
        },
      })
      .catch((error) => console.warn('Gift quiz: the taste step was not saved to People', error))
  }
}

/** Skip leaves the card as it is: this step's unsaved picks go back to the saved ones. */
function revertTasteStep() {
  const saved = personTaste.get(recipient.value.id)
  if (step.value === 'styles') stylePicks.value = [...saved.styleIds]
  if (step.value === 'colors') colorPicks.value = [...saved.colorIds]
  if (step.value === 'brands') brandPicks.value = [...saved.brandIds]
}

/** 07b preview — what is saved on the card now. */
const savedTaste = computed(() => tasteResolver.resolve(personTaste.get(recipient.value.id)))

const styleItems = computed<FairProfileV2PickerItem[]>(() =>
  (stylesQuery.data.value ?? []).map((style) => ({
    id: style.id,
    name: style.name,
    image: style.image ?? undefined,
    imageSrcset: style.imageSrcset ?? undefined,
  })),
)
const colorItems = computed<FairProfileV2PickerItem[]>(() =>
  (colorsQuery.data.value ?? []).map((color) => ({ id: color.id, name: color.name, hex: color.hex, family: color.family })),
)
const brandItems = computed<FairProfileV2PickerItem[]>(() =>
  (brandsQuery.data.value ?? []).map((brand) => ({ id: brand.id, name: brand.name, image: brand.logo, popular: brand.popular })),
)

function namesOf(items: FairProfileV2PickerItem[], ids: number[]): string[] {
  return ids.map((id) => items.find((item) => item.id === id)?.name).filter((name): name is string => Boolean(name))
}

const occasionLabel = computed(
  () => occasionOptions.value.find((option) => option.value === answers.value.occasion)?.label ?? '',
)
const budgetLabels = computed(() => ({ under: t('gift_quiz.budget.under'), notSure: t('gift_quiz.budget.not_sure') }))
const budgetOptions = computed(() => giftQuizBudgetOptions(giftConfig.value, currency.value, budgetLabels.value))
const budgetLabel = computed(
  () => budgetOptions.value.find((option) => option.value === answers.value.budget)?.label ?? '',
)
const chips = computed(() =>
  giftQuizAnswerChips(
    { ...answers.value, styleNames: namesOf(styleItems.value, stylePicks.value) },
    occasionLabel.value,
    budgetLabel.value,
  ),
)

/* ── Search ───────────────────────────────────────────────────────────── */

/** The chosen band's range in the shown currency ("Not sure" → no price filter). */
function budgetRange(): { min: number | null; max: number | null } {
  const budget = budgetOptions.value.find((option) => option.value === answers.value.budget)
  return { min: budget?.min ?? null, max: budget?.max ?? null }
}

const cards = ref<FairSearchV2ProductCard[]>([])
let abort: AbortController | null = null

/** Decision 4 — the backend's gift ideas for a saved person; the quiz's own search otherwise. */
const giftIdeasApi = hasSurveysApi('gift_ideas')

async function findGifts() {
  const saved = saveTasteToPerson()
  const full: GiftQuizAnswers = {
    ...answers.value,
    styleNames: namesOf(styleItems.value, stylePicks.value),
    colorNames: namesOf(colorItems.value, colorPicks.value),
    brandIds: [...brandPicks.value],
    brandNames: namesOf(brandItems.value, brandPicks.value),
  }
  step.value = 'loading'
  abort?.abort()
  abort = new AbortController()
  const signal = abort.signal
  try {
    const personId = recipient.value.id
    let source: 'ml' | 'fallback' | 'search' = 'search'
    if (giftIdeasApi && giftApi && personId !== null) {
      // The server reads the person's taste from People: the card is written first.
      await saved
      const response = await fetchGiftIdeas(giftQuizIdeasParams(personId, full, budgetRange()), signal)
      if (response.source !== 'ml' && response.reason) console.info('Gift ideas: fallback —', response.reason)
      source = response.source === 'ml' ? 'ml' : 'fallback'
      cards.value = response.items.map(mapFairSearchV2TeaserToGridCard)
    } else {
      const response = await fetchGiftQuizIdeas(
        giftQuizSearchRequest(recipient.value, full, budgetRange(), occasionLabel.value),
        signal,
      )
      cards.value = (response.results?.items ?? []).map(mapFairSearchV2TeaserToGridCard)
    }
    if (!cards.value.length) {
      step.value = 'empty'
      return
    }
    store.setResult({ recipientName: recipient.value.name, chips: chips.value, cards: cards.value, source })
    step.value = 'preview'
  } catch (error) {
    if ((error as { name?: string })?.name === 'CanceledError' || signal.aborted) return
    console.error('Gift quiz: ideas did not load', error)
    step.value = 'error'
  }
}

/** X on Loading cancels the search (12 note). */
function cancelSearch() {
  abort?.abort()
  goTo('note')
}

/* ── Result actions ───────────────────────────────────────────────────── */

const savingList = ref(false)

async function saveToList() {
  if (savingList.value) return
  savingList.value = true
  const name = t('gift_quiz.list_name', { name: recipient.value.name })
  try {
    await saveGiftQuizIdeas(name, cards.value.map((card) => card.variantId))
    toast.show({ tone: 'success', message: t('gift_quiz.preview.saved', { name }) })
  } catch (error) {
    console.error('Gift quiz: list not saved', error)
    toast.show({ tone: 'warning', message: t('gift_quiz.preview.save_error') })
  } finally {
    savingList.value = false
  }
}

async function seeAll() {
  store.close()
  await settleFairQuizHistory()
  await router.push({ name: 'gift-ideas' })
}

/** 14 Change answers: back to step 1 with the answers kept. */
function changeAnswers() {
  goTo('who')
}

/* ── Flow ─────────────────────────────────────────────────────────────── */

function goTo(next: GiftQuizStep) {
  step.value = next
}

/**
 * Next question. Leaving Budget decides 07b (T-135); the end of Tune only / Add person
 * saves and closes with the toast; the end of Find a gift is Note's search.
 */
function forward() {
  if (step.value === 'budget') {
    tasteCheck.value = personTaste.isTuned(recipient.value.id)
    changeTaste.value = false
  }
  const next = giftQuizStepAfter(steps.value, step.value, 1)
  if (next) goTo(next)
  else finish()
}

function back() {
  const previous = giftQuizStepAfter(steps.value, step.value, -1)
  if (previous) goTo(previous)
}

/** Continue on 08–10: save the step, then on. */
function continueTaste() {
  saveTasteStep()
  forward()
}

/** 07b Continue: the taste as is, straight to Note. */
function useSavedTaste() {
  changeTaste.value = false
  prefillTaste()
  forward()
}

/** 07b Change taste: 08–10 prefilled from the card. */
function editTaste() {
  changeTaste.value = true
  prefillTaste()
  forward()
}

/** Save on 10b (Tune only) / the last step of Add person: back to People with a toast. */
function finish() {
  const id = recipient.value.id
  const name = recipient.value.name
  if (id === null) {
    store.close()
    return
  }
  const kind = store.mode === 'add-person' ? 'added' : 'tuned'
  toast.show({
    tone: 'success',
    message: kind === 'added' ? t('profile.people_page.added', { name }) : t('gift_quiz.tune_saved', { name }),
    life: TOAST_LIFE_MS,
  })
  store.finish({ kind, personId: id, name })
}

const progress = computed(() => giftQuizProgress(step.value, steps.value))
const headerRight = computed(() =>
  ['styles', 'colors', 'brands', 'note'].includes(step.value) ? 'skip' : 'none',
)
const leaveOpen = ref(false)
/** 16: a person saved after step 1 stays in People — the dialog says so (R288). */
const leaveText = computed(() =>
  createdId.value !== null
    ? t('gift_quiz.leave.text_saved', { name: recipient.value.name })
    : t('gift_quiz.leave.text'),
)

function requestClose() {
  if (step.value === 'loading') {
    cancelSearch()
    return
  }
  if (['welcome', 'preview', 'empty', 'error'].includes(step.value)) {
    store.close()
    return
  }
  leaveOpen.value = true
}

/** Leaving never creates anything; what was saved stays, no toast (R288). */
function exit() {
  leaveOpen.value = false
  store.close()
}

function skip() {
  if (step.value === 'note') {
    void findGifts()
    return
  }
  revertTasteStep()
  forward()
}

/* ── Open ─────────────────────────────────────────────────────────────── */

/** Find a gift for a known person starts at Occasion; Tune only — at 08 Styles. */
function startStep(preset: Person | undefined): GiftQuizStep {
  if (store.mode === 'tune') return 'styles'
  if (store.mode === 'add-person') return 'who'
  return preset ? 'occasion' : 'welcome'
}

function presetPerson(): Person | undefined {
  return store.presetPersonId === null ? undefined : people.value.find((person) => person.id === store.presetPersonId)
}

watch(
  () => store.session,
  async () => {
    currency.value = readStoredCurrency()
    // Occasion, budget and note are asked every time (16 note).
    answers.value = emptyGiftQuizAnswers()
    draft.value = emptyFairProfileV2PersonDraft()
    cards.value = []
    createdId.value = null
    tasteCheck.value = false
    changeTaste.value = false
    selectedId.value = store.mode === 'add-person' ? null : store.presetPersonId
    recipient.value = { id: store.mode === 'add-person' ? null : store.presetPersonId, name: '', audience: null }
    // People may be cached: start with it, then refresh for the name / gender.
    const cached = presetPerson()
    if (cached) recipient.value = recipientOf(cached)
    prefillTaste()
    step.value = startStep(cached)
    await peopleQuery.refresh(true)
    const fresh = presetPerson()
    if (fresh && store.mode !== 'add-person') recipient.value = recipientOf(fresh)
    // A deleted / unknown person: Find a gift falls back to the start.
    if (!fresh && store.mode === 'gift' && step.value === 'occasion') step.value = 'welcome'
    else if (!cached && fresh && store.mode === 'gift' && step.value === 'welcome') step.value = 'occasion'
  },
  { immediate: true },
)
</script>

<template>
  <FairQuizLayer
    :open="store.isOpen"
    class="gift-quiz"
    :class="`gift-quiz--${step}`"
    :aria-label="t('gift_quiz.aria')"
    @close-request="requestClose"
  >
    <FairQuizHeader
      :progress="progress"
      :right="headerRight"
      :skip-label="t('quiz.skip')"
      :close-label="t('quiz.close')"
      :progress-label="t('quiz.progress')"
      @close="requestClose"
      @skip="skip"
    />

    <FairQuizWelcome
      v-if="step === 'welcome'"
      :title="t('gift_quiz.welcome.title')"
      :subtitle="t('gift_quiz.welcome.subtitle')"
      :pills="[{ label: t('gift_quiz.welcome.pill_time'), star: false }]"
      :continue-label="t('quiz.continue')"
      @continue="goTo('who')"
    />

    <!-- 01–04 Who -->
    <FairQuizStep
      v-else-if="step === 'who'"
      fill
      :title="store.mode === 'add-person' ? t('gift_quiz.who.title_add') : t('gift_quiz.who.title')"
      :subtitle="whoPeople.length ? t('gift_quiz.who.subtitle') : t('gift_quiz.who.subtitle_empty')"
      :show-back="false"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!canContinueWho"
      :continue-loading="saving"
      @continue="continueWho"
    >
      <GiftQuizWho
        v-model:selected-id="selectedId"
        v-model:draft="draft"
        :people="whoPeople"
        :compact="compact"
      />
    </FairQuizStep>

    <!-- 05 / 06 Occasion (Other opens a text field) -->
    <FairQuizStep
      v-else-if="step === 'occasion'"
      fill
      :title="t('gift_quiz.occasion.title')"
      :subtitle="t('gift_quiz.occasion.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!answers.occasion"
      @back="back"
      @continue="forward"
    >
      <div class="gift-quiz__content">
        <div
          class="gift-quiz__cards"
          role="radiogroup"
          :aria-label="t('gift_quiz.occasion.title')"
        >
          <FairThemeCard
            v-for="option in occasionOptions"
            :key="option.value"
            :label="option.label"
            :icon="option.icon"
            :selected="answers.occasion === option.value"
            @click="answers = { ...answers, occasion: option.value, occasionText: option.value === 'other' ? answers.occasionText : '' }"
          />
        </div>
        <FairInputText
          v-if="answers.occasion === 'other'"
          v-model="answers.occasionText"
          variant="underline"
          :placeholder="t('gift_quiz.occasion.custom')"
        />
      </div>
    </FairQuizStep>

    <!-- 07 Budget: same cards without icons -->
    <FairQuizStep
      v-else-if="step === 'budget'"
      fill
      :title="t('gift_quiz.budget.title')"
      :subtitle="t('gift_quiz.budget.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!answers.budget"
      @back="back"
      @continue="forward"
    >
      <div
        class="gift-quiz__cards"
        role="radiogroup"
        :aria-label="t('gift_quiz.budget.title')"
      >
        <FairThemeCard
          v-for="option in budgetOptions"
          :key="option.value"
          :label="option.label"
          :selected="answers.budget === option.value"
          @click="answers = { ...answers, budget: option.value }"
        />
      </div>
    </FairQuizStep>

    <!-- 07b Taste check (T-135): a tuned person's taste instead of 08–10 -->
    <FairQuizStep
      v-else-if="step === 'taste'"
      fill
      :title="t('gift_quiz.taste_check.title', { name: recipient.name })"
      :subtitle="t(`gift_quiz.taste_check.subtitle_${giftQuizPronoun(recipient.audience)}`)"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      @back="back"
      @continue="useSavedTaste"
    >
      <div class="gift-quiz__taste-check">
        <div class="gift-quiz__taste">
          <FairTasteDetails
            :styles="savedTaste.styles"
            :colors="savedTaste.colors"
            :brands="savedTaste.brands"
            :loading="tasteResolver.isLoading.value"
          />
        </div>
        <FairButton
          class="gift-quiz__change-taste"
          variant="flat"
          size="auto"
          :label="t('gift_quiz.taste_check.change')"
          @click="editTaste"
        />
      </div>
    </FairQuizStep>

    <!-- 08–10 the person's Styles / Colors / Brands; 10b Save ends Tune only / Add person -->
    <FairQuizStep
      v-else-if="step === 'styles' || step === 'colors' || step === 'brands'"
      fill
      :class="{ 'style-quiz__styles': step === 'styles' }"
      :title="t(`gift_quiz.${step}.title`, { name: recipient.name })"
      :subtitle="t('gift_quiz.pick_any_saved', { name: recipient.name })"
      :show-back="giftQuizStepAfter(steps, step, -1) !== null"
      :back-label="t('quiz.back')"
      :continue-label="isLastStep ? t('gift_quiz.save') : t('quiz.continue')"
      @back="back"
      @continue="continueTaste"
    >
      <FairProfileV2SelectionPicker
        v-if="step === 'styles'"
        v-model="stylePicks"
        kind="styles"
        :items="styleItems"
        :loading="stylesQuery.isLoading.value"
        :error="Boolean(stylesQuery.error.value)"
        @retry="stylesQuery.refresh(true)"
      />
      <FairProfileV2SelectionPicker
        v-else-if="step === 'colors'"
        v-model="colorPicks"
        kind="colors"
        :items="colorItems"
        :loading="colorsQuery.isLoading.value"
        :error="Boolean(colorsQuery.error.value)"
        @retry="colorsQuery.refresh(true)"
      />
      <FairProfileV2SelectionPicker
        v-else
        v-model="brandPicks"
        kind="brands"
        :items="brandItems"
        :loading="brandsQuery.isLoading.value"
        :error="Boolean(brandsQuery.error.value)"
        @retry="brandsQuery.refresh(true)"
      />
    </FairQuizStep>

    <!-- 11 Note -->
    <FairQuizStep
      v-else-if="step === 'note'"
      fill
      :title="t('gift_quiz.note.title')"
      :subtitle="t('gift_quiz.note.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('gift_quiz.note.find')"
      @back="back"
      @continue="findGifts"
    >
      <FairInputText
        v-model="answers.note"
        variant="underline"
        :placeholder="t('gift_quiz.note.placeholder')"
      />
    </FairQuizStep>

    <!-- 12 Loading: skeleton, × cancels -->
    <FairQuizStep
      v-else-if="step === 'loading'"
      fill
      :title="t('gift_quiz.loading.title')"
      :subtitle="t('gift_quiz.loading.subtitle', { name: recipient.name })"
      :show-footer="false"
    >
      <div
        class="gift-quiz__skeleton"
        aria-busy="true"
      >
        <FairSkeleton
          v-for="index in 3"
          :key="index"
          class="gift-quiz__skeleton-card"
          variant="media"
          :mark="false"
        />
      </div>
    </FairQuizStep>

    <!-- 13 Preview -->
    <GiftQuizPreview
      v-else-if="step === 'preview'"
      :name="recipient.name"
      :chips="chips"
      :cards="cards"
      :compact="compact"
      :saving="savingList"
      @save="saveToList"
      @see-all="seeAll"
    />

    <!-- 14 No ideas -->
    <FairQuizStep
      v-else-if="step === 'empty'"
      :title="t('gift_quiz.empty.title')"
      :subtitle="t('gift_quiz.empty.subtitle')"
      :show-footer="false"
    >
      <div class="gift-quiz__empty">
        <div class="gift-quiz__chips">
          <FairChip
            v-for="chip in chips"
            :key="chip"
            :label="chip"
            :size="compact ? 'mobile' : 'default'"
          />
        </div>
        <FairButton
          class="gift-quiz__change"
          variant="tonal"
          size="auto"
          :label="t('gift_quiz.empty.change')"
          @click="changeAnswers"
        />
      </div>
    </FairQuizStep>

    <!-- 15 Error -->
    <FairQuizStep
      v-else-if="step === 'error'"
      :title="t('gift_quiz.error.title')"
      :subtitle="t('gift_quiz.error.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('gift_quiz.error.retry')"
      @back="goTo('note')"
      @continue="findGifts"
    />

    <FairQuizLeaveDialog
      v-model:visible="leaveOpen"
      :title="t('gift_quiz.leave.title')"
      :text="leaveText"
      :keep-label="t('gift_quiz.leave.keep')"
      :exit-label="t('gift_quiz.leave.exit')"
      :close-label="t('quiz.close')"
      @exit="exit"
    />
  </FairQuizLayer>
</template>
